import { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchPostApplicationCustomerConfirmedProvidingDocs } from '@iwoca/lapi-client/edge';

import { useOffers } from './useOffers';
import { useGetApplicationQuery } from '../../api/lendingApi/lendingApiQueries';
import { getMarketingPollUpdatedState } from '../../components/helpers/stateNormalisation';
import { SELF_SERVE_DATA_REQUIREMENT_ERRORS } from '../../constants';
import { TGetOffersResponse, TGetStateResponse } from '../../types';
import logger from '../../utils/logger';
import { useContextStateKey } from '../useContextStateKey';
import { useSignupParams } from '../useSignupParams';
import { useSignupRouterUK } from '../useSignupRouter/useSignupRouterUK';

export const useOffersUK = ({ enabled }: { enabled: boolean }) => {
  const hasRunRedirectionRef = useRef(false);

  const [isDeciding, setIsDeciding] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(true);

  const {
    isLoadingOffers,
    getDataRequirementActions,
    getErroredDataRequirements,
    getSignupError,
    offers,
    getDocumentRequirements,
    isAutoEligible,
    checkDocumentsRequired,
    refetchOffers,
    isRefetchingOffers,
    offerStatus,
  } = useOffers({ enabled: enabled });

  const { application } = useGetApplicationQuery();

  const stateKey = useContextStateKey();
  const { currentStep, navigateSignup, navigateToAccountPages } =
    useSignupRouterUK();
  const { isDirectCustomer } = useSignupParams();

  const queryClientState = () =>
    getMarketingPollUpdatedState<TGetStateResponse>();

  // Redirection useEffect for offers page
  useEffect(() => {
    if (
      currentStep !== 'offers' ||
      !stateKey ||
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      !application ||
      hasRunRedirectionRef.current
    ) {
      return;
    }

    const handleRedirect = (
      stateKey: string,
      offers: TGetOffersResponse['data']['offers'],
    ) => {
      hasRunRedirectionRef.current = true;

      const dataRequirementActions = getDataRequirementActions(offers);

      const dataRequirementErrors = getErroredDataRequirements(
        dataRequirementActions,
      );

      if (dataRequirementActions && dataRequirementActions.length > 0) {
        tracking.ampli.dataRequirementActionsRaised({
          recommended_resolution_methods: [
            // Multiple products have the same actions, so removing duplicates
            ...new Set(
              dataRequirementActions.map(
                (action) => action.recommended_resolution_method,
              ),
            ),
          ],
        });
      }

      if (dataRequirementErrors && dataRequirementErrors.length > 0) {
        tracking.ampli.dataRequirementsErrored({
          recommended_resolution_methods: dataRequirementErrors,
        });
      }

      const hasDocumentsRequired = checkDocumentsRequired(
        getDocumentRequirements(offers),
      );

      if (hasSelfServeDataRequirementError(dataRequirementErrors)) {
        const updatedStateData = queryClientState();
        return void navigateSignup({
          route: 'check',
          updatedState: updatedStateData,
        });
      }

      const signupError = getSignupError(offers);

      if (signupError || shouldShowOffers(offers)) {
        return setIsLoadingNextPage(false);
      }

      const noOffersAvailable = offerStatus !== 'offers_available';

      const redirectToDecision = noOffersAvailable || !hasDocumentsRequired;
      const redirectToDocuments = hasDocumentsRequired;

      if (redirectToDecision) {
        if (isDirectCustomer) {
          const updatedStateData = queryClientState();

          void navigateToAccountPages(stateKey, updatedStateData);
        } else {
          const updatedStateData = queryClientState();

          void navigateSignup({
            route: 'decision',
            updatedState: updatedStateData,
          });
        }
      } else if (redirectToDocuments) {
        const updatedStateData = queryClientState();

        void navigateSignup({
          route: 'documents',
          updatedState: updatedStateData,
        });
      }
    };

    void handleRedirect(stateKey, offers);
  }, [isLoadingOffers, offers, stateKey]);

  const handleDecision = async () => {
    setIsDeciding(true);

    if (!stateKey) {
      return logger.error('Attempted to handle decision without stateKey.');
    }

    await fetchPostApplicationCustomerConfirmedProvidingDocs({
      stateKey,
    });

    const refetchedOffers = await refetchOffers();

    const documentRequirements = getDocumentRequirements(refetchedOffers);
    const hasDocumentsStillRequired = checkDocumentsRequired(
      documentRequirements,
      { allowSubmissionWithErrors: true },
    );
    const dataRequirementActions = getDataRequirementActions(refetchedOffers);

    if (!hasDocumentsStillRequired && stateKey) {
      if (isDirectCustomer) {
        const updatedStateData = queryClientState();

        return void navigateToAccountPages(stateKey, updatedStateData);
      }

      const dataRequirementErrors = getErroredDataRequirements(
        dataRequirementActions,
      );

      if (hasSelfServeDataRequirementError(dataRequirementErrors)) {
        return void navigateSignup({ route: 'check-skipped' });
      }

      await navigateSignup({
        route: 'decision',
      });
    }

    setIsDeciding(false);
  };

  const hasSelfServeDataRequirementError = (
    dataRequirementErrors: ReturnType<typeof getErroredDataRequirements>,
  ) => {
    return dataRequirementErrors?.some((error) =>
      SELF_SERVE_DATA_REQUIREMENT_ERRORS.includes(error),
    );
  };

  const filterConfirmedOffers = (
    offers: TGetOffersResponse['data']['offers'],
  ) => {
    return offers.filter((offer) => offer.offer_type === 'confirmed');
  };

  const hasOnlyConfirmedOffers = (
    offers?: TGetOffersResponse['data']['offers'],
  ) => {
    if (!offers) {
      return false;
    }

    return (
      filterConfirmedOffers(offers).length > 0 &&
      filterConfirmedOffers(offers).length === offers.length
    );
  };

  // To be removed if we no longer show microapprovals.
  const shouldShowOffers = (offers?: TGetOffersResponse['data']['offers']) => {
    if (!offers) {
      return false;
    }

    const hasConfirmedOffers = filterConfirmedOffers(offers).length > 0;

    const hasMicroApproval =
      hasConfirmedOffers && !hasOnlyConfirmedOffers(offers);

    const hasOfferSelected = application?.has_had_offer_selected;

    return hasMicroApproval && !hasOfferSelected;
  };

  return {
    signupError: getSignupError(offers),
    offers,
    documentRequirements: getDocumentRequirements(offers),
    hasDocumentRequirements: checkDocumentsRequired(
      getDocumentRequirements(offers),
    ),
    dataRequirementErrors: getErroredDataRequirements(
      getDataRequirementActions(offers),
    ),
    isAutoEligible,
    handleDecision,
    isDeciding,
    isLoadingOffers,
    isRefetchingOffers,
    shouldShowOffers: shouldShowOffers(offers),
    isLoadingOrRedirecting:
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      isLoadingNextPage ||
      offerStatus === 'declined' ||
      offerStatus === 'deferred' ||
      (!shouldShowOffers(offers) && !getSignupError(offers)),
  };
};
